import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useApiClient from '../hooks/useApiClient';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const formatDate = (dateString) => {
	if (!dateString) return '_________';
	const date = new Date(dateString);
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

const parseNormalized = (value, fallback = 0) => {
	if (value === null || value === undefined) return fallback;
	const n = Number(value);
	if (Number.isNaN(n)) return fallback;
	if (n < 0) return 0;
	if (n > 1) return 1;
	return n;
};

const PrintableEntrySheet = () => {
	const apiClient = useApiClient();
	const [searchParams] = useSearchParams();
	const entrySheetId = searchParams.get('id');
	const [entrySheet, setEntrySheet] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isDownloading, setIsDownloading] = useState(false);

	// For damage image overlay calculations
	const damageContainerRef = useRef(null);
	const damageImageRef = useRef(null);
	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
	const [imageNatural, setImageNatural] = useState({ width: 0, height: 0 });

	useEffect(() => {
		const loadEntrySheet = async () => {
			if (!entrySheetId) {
				setError('Lipsește parametrul id în URL.');
				setLoading(false);
				return;
			}
			setLoading(true);
			setError(null);
			try {
				const response = await apiClient.get(`/entrySheet/one?id=${entrySheetId}`);
				setEntrySheet(response);
			} catch (err) {
				console.error('Eroare la încărcarea fișei de intrare:', err);
				setError('A apărut o eroare la încărcarea datelor fișei de intrare.');
				setEntrySheet(null);
			}
			setLoading(false);
		};

		loadEntrySheet();
	}, [entrySheetId, apiClient]);

	useEffect(() => {
		const measure = () => {
			const containerEl = damageContainerRef.current;
			if (!containerEl) return;
			const rect = containerEl.getBoundingClientRect();
			if (rect.width && rect.height) {
				setContainerSize({ width: rect.width, height: rect.height });
			}
		};
		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [entrySheet]);

	const computePosition = (nx, ny) => {
		const cw = containerSize.width;
		const ch = containerSize.height;
		const iw = imageNatural.width;
		const ih = imageNatural.height;
		if (!cw || !ch || !iw || !ih) return null;
		const scale = Math.min(cw / iw, ch / ih);
		const drawnW = iw * scale;
		const drawnH = ih * scale;
		const left = (cw - drawnW) / 2 + nx * drawnW;
		const top = (ch - drawnH) / 2 + ny * drawnH;
		return { left, top };
	};

	const flags = useMemo(() => ([
		{ key: 'hasFirstAidKit', label: 'Trusă medicală' },
		{ key: 'hasReflectiveTriangle', label: 'Triunghi reflectorizant' },
		{ key: 'hasLiftingJack', label: 'Cric' },
		{ key: 'hasSpareWheel', label: 'Roată de rezervă' },
		{ key: 'hasCheckEngineLight', label: 'Check Engine aprins' },
		{ key: 'hasABSLight', label: 'ABS aprins' },
		{ key: 'hasParkingBrakeLight', label: 'Frână parcare aprinsă' },
		{ key: 'hasUnknownDefectsLight', label: 'Martor necunoscut aprins' },
		{ key: 'hasReplacedParts', label: 'Piese înlocuite' },
		{ key: 'hasPhoneAgreement', label: 'Acord telefonic' },
		{ key: 'hasRoadTestAgreement', label: 'Acord test rutier' },
	]), []);

	const downloadPDF = async () => {
		if (isDownloading) return;
		setIsDownloading(true);
		try {
			const pages = document.querySelectorAll('.page');
			if (!pages.length) throw new Error('No pages found');
			const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
			for (let i = 0; i < pages.length; i++) {
				const pageElement = pages[i];
				const canvas = await html2canvas(pageElement, {
					scale: 2,
					useCORS: true,
					letterRendering: true,
					allowTaint: true,
					backgroundColor: '#ffffff',
					width: pageElement.offsetWidth,
					height: pageElement.offsetHeight,
					windowWidth: pageElement.offsetWidth,
					windowHeight: pageElement.offsetHeight
				});
				const imgData = canvas.toDataURL('image/jpeg', 0.95);
				if (i > 0) doc.addPage();
				doc.addImage(imgData, 'JPEG', 0, 0, 210, 297);
			}
			const seriesNumber = entrySheet?.seriesNumber || 'N-A';
			const dateStr = formatDate(entrySheet?.date).replace(/\//g, '-');
			const filename = `Fisa_intrare_${seriesNumber}_${dateStr}.pdf`;
			doc.save(filename);
		} catch (e) {
			console.error('Error generating PDF:', e);
			alert('A apărut o eroare la generarea PDF-ului. Vă rugăm să încercați din nou.');
		} finally {
			setIsDownloading(false);
		}
	};

	if (loading) {
		return <div className="p-8 text-center">Se încarcă fișa de intrare...</div>;
	}

	if (error) {
		return (
			<div className="p-8 text-center">
				<h1 className="text-xl font-bold text-red-600 mb-4">Eroare la încărcare</h1>
				<p className="text-gray-700">{error}</p>
			</div>
		);
	}

	if (!entrySheet) {
		return <div className="p-8 text-center">Nu s-au găsit date pentru fișa de intrare.</div>;
	}

	return (
		<div className="printable-container text-gray-800 font-sans text-xs">
			<style>
				{`
					body {
						background-color: #f0f0f0;
					}
					.printable-container {
						width: 210mm;
						margin: 20px auto;
						padding: 15mm;
						box-shadow: 0 0 10px rgba(0,0,0,0.1);
						border: 1px solid #ccc;
						background: white;
						max-height: 90vh;
						overflow-y: auto;
						overflow-x: hidden;
					}
					.document-content {
						width: 100%;
					}
					.page-footer {
						width: 100%;
						background: #f8f9fa;
						border-top: 2px solid #2c3e50;
						border-bottom: 2px solid #2c3e50;
						padding: 10px 15px;
						box-sizing: border-box;
						margin-top: 30px;
						page-break-inside: avoid;
						break-inside: avoid;
					}
					@media print {
						* {
							-webkit-print-color-adjust: exact !important;
							print-color-adjust: exact !important;
							color-adjust: exact !important;
						}
						html, body {
							margin: 0 !important;
							padding: 0 !important;
							background-color: #fff !important;
							width: 100% !important;
							height: auto !important;
							overflow: visible !important;
							position: static !important;
						}
						a[href]:after {
							content: none !important;
						}
						.printable-container {
							width: 100%;
							margin: 0;
							padding: 10mm;
							box-shadow: none;
							border: none;
							max-height: none;
							overflow: visible;
						}
						.no-print {
							display: none !important;
						}
						.page-footer {
							padding: 10px;
							page-break-inside: avoid;
							break-inside: avoid;
						}
						table {
							page-break-inside: auto;
						}
						tbody tr {
							page-break-inside: avoid;
							break-inside: avoid;
						}
					}
					table {
						width: 100%;
						border-collapse: collapse;
						margin-top: 10px;
					}
					th, td {
						padding: 4px 6px;
						text-align: left;
					}
					.header-main {
						background-color: #2c3e50;
						color: white;
						padding: 15px;
						margin-bottom: 20px;
						page-break-inside: avoid;
						break-inside: avoid;
					}
					.header-main h1 {
						font-size: 1.8em;
						font-weight: bold;
						margin:0;
					}
					.header-main p {
						font-size: 0.9em;
						margin:0;
					}
					.details-grid {
						display: grid;
						grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
						gap: 20px;
						margin-bottom: 20px;
						page-break-inside: avoid;
						break-inside: avoid;
					}
					.details-box {
						border: 1px solid #333;
						padding: 12px;
						page-break-inside: avoid;
						break-inside: avoid;
					}
					.details-box h3 {
						font-weight: bold;
						margin-bottom: 5px;
						font-size: 0.9em;
						text-transform: uppercase;
					}
					.items-table th {
						border-bottom: 1px solid #555;
						font-weight: bold;
					}
					.items-table td {
						border-bottom: 1px dotted #ccc;
					}
					.items-table .text-right { text-align: right; }
					.total-row td {
						font-weight: bold;
						border-top: 2px solid #333;
						padding-top: 8px;
					}
					.items-table tbody tr {
						page-break-inside: avoid;
						break-inside: avoid;
					}
					.footer-notes {
						margin-top: 20px;
						font-size: 0.8em;
						color: #555;
					}
					.section-title {
						font-weight: bold;
						text-transform: uppercase;
						margin-top: 8px;
						margin-bottom: 4px;
						font-size: 0.9em;
					}
					.mb-4 {
						margin-bottom: 16px;
					}
					.damage-canvas {
						width: 100%;
						height: 200px;
						background: #f8f9fa;
						border: 1px solid #333;
						border-radius: 6px;
						position: relative;
						overflow: hidden;
						margin-bottom: 10px;
					}
					.damage-canvas img {
						width: 100%;
						height: 100%;
						object-fit: contain;
					}
					.damage-dot {
						position: absolute;
						width: 12px;
						height: 12px;
						background: #ef4444;
						border: 2px solid white;
						border-radius: 9999px;
						transform: translate(-50%, -50%);
						box-shadow: 0 2px 4px rgba(0,0,0,0.3);
					}
					.photos-grid {
						display: grid;
						grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
						gap: 8px;
						margin-top: 8px;
					}
					.photo-item {
						border: 1px solid #333;
						border-radius: 4px;
						overflow: hidden;
						background: #f8f9fa;
					}
					.photo-item img {
						width: 100%;
						height: auto;
						min-height: 120px;
						max-height: 200px;
						object-fit: contain;
					}
					.flags-grid {
						display: grid;
						grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
						gap: 6px;
						margin-top: 8px;
					}
					.flag-item {
						display: flex;
						justify-content: space-between;
						align-items: center;
						padding: 4px 8px;
						background: #f8f9fa;
						border-radius: 4px;
					}
				`}
			</style>

			<div className="no-print mb-6 flex gap-3">
				<button onClick={() => window.print()} className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">Printează / Salvează PDF</button>
				<button onClick={downloadPDF} disabled={isDownloading} className="p-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">{isDownloading ? 'Se generează PDF...' : 'Descarcă ca PDF'}</button>
			</div>

			<div className="document-content">
				<div className="header-main">
					<h1>FIȘĂ DE INTRARE</h1>
					<p className='py-2'>Data: {formatDate(entrySheet.date)}</p>
					{entrySheet.seriesNumber && <p>Serie și număr: {entrySheet.seriesNumber}</p>}
					{entrySheet.status && <p>Status: {entrySheet.status}</p>}
				</div>

				<div className="details-grid">
					<div className="details-box">
						<h3>PRESTATOR</h3>
						<p><strong>{entrySheet.shopBusiness?.name || '-'}</strong></p>
						{entrySheet.shopBusiness?.tax_identifier && <p>CUI: {entrySheet.shopBusiness.tax_identifier}</p>}
						{entrySheet.shopBusiness?.reg_com && <p>Reg. com.: {entrySheet.shopBusiness.reg_com}</p>}
						<p>Adresa: {entrySheet.shopBusiness?.address || '-'}</p>
					</div>
					<div className="details-box">
						<h3>CLIENT</h3>
						<p><strong>{entrySheet.clientData?.name || '-'}</strong></p>
						{entrySheet.clientData?.taxIdentifier && <p>CUI/CNP: {entrySheet.clientData.taxIdentifier}</p>}
						{entrySheet.clientData?.regCom && <p>Reg. com.: {entrySheet.clientData.regCom}</p>}
						{entrySheet.clientData?.address && <p>Adresa: {entrySheet.clientData.address}</p>}
					</div>
					<div className="details-box">
						<h3>VEHICUL</h3>
						<p>Marcă: {entrySheet.clientData?.make || '-'}</p>
						<p>Model: {entrySheet.clientData?.type || '-'}</p>
						<p>Nr. înmatriculare: {entrySheet.clientData?.registrationNumber || '-'}</p>
						{entrySheet.clientData?.vin && <p>VIN: {entrySheet.clientData.vin}</p>}
					</div>
				</div>

				<div className="details-box mb-4">
					<h3 className="section-title">Constatări</h3>
					<table className="items-table">
						<tbody>
							<tr>
								<th style={{ width: '220px' }}>Defecte Client</th>
								<td>{entrySheet.report?.clientDefect || entrySheet.clientDefect || '-'}</td>
							</tr>
							<tr>
								<th>Observații</th>
								<td>{entrySheet.report?.observations || entrySheet.observations || '-'}</td>
							</tr>
						</tbody>
					</table>
					<div className="flags-grid">
						{flags.map(item => (
							<div key={item.key} className="flag-item">
								<span>{item.label}</span>
								<span style={{ fontWeight: '600' }}>{entrySheet.report?.[item.key] ? 'Da' : 'Nu'}</span>
							</div>
						))}
					</div>
				</div>

				{Array.isArray(entrySheet.report?.carDamages) && entrySheet.report.carDamages.length > 0 && (
					<div className="details-box mb-4">
						<h3 className="section-title">Avarii marcate</h3>
						<div ref={damageContainerRef} className="damage-canvas">
							<img
								ref={damageImageRef}
								src="/car-parts.svg"
								alt="Schema vehicul pentru avarii"
								onLoad={(e) => setImageNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
							/>
							{containerSize.width > 0 && containerSize.height > 0 && imageNatural.width > 0 && imageNatural.height > 0 && entrySheet.report.carDamages.map((damage, index) => {
								const nx = parseNormalized(damage.x ?? damage.nx, 0);
								const ny = parseNormalized(damage.y ?? damage.ny, 0);
								const pos = computePosition(nx, ny);
								if (!pos) return null;
								return (
									<div key={index} className="damage-dot" style={{ left: `${pos.left}px`, top: `${pos.top}px` }} title={`${damage.partName || ''}${damage.damageType ? ' - ' + damage.damageType : ''}${damage.notes ? ' (' + damage.notes + ')' : ''}`} />
								);
							})}
						</div>
						<table className="items-table" style={{ marginTop: '8px' }}>
							<thead>
								<tr>
									<th>#</th>
									<th>Zonă</th>
									<th>Tip avarie</th>
									<th>Note</th>
								</tr>
							</thead>
							<tbody>
								{entrySheet.report.carDamages.map((d, idx) => (
									<tr key={idx}>
										<td>{idx + 1}</td>
										<td>{d.partName || '-'}</td>
										<td>{d.damageType || '-'}</td>
										<td>{d.notes || '-'}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{Array.isArray(entrySheet.photosUrls) && entrySheet.photosUrls.length > 0 && (
					<div className="details-box mb-4">
						<h3 className="section-title">Fotografii</h3>
						<div className="photos-grid">
							{entrySheet.photosUrls.map((url, idx) => (
								<div key={idx} className="photo-item">
									<img src={url} alt={`Foto ${idx + 1}`} />
								</div>
							))}
						</div>
					</div>
				)}

				<div className="page-footer">
					<div className="flex justify-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
						<div>
							<h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Client</h3>
							<p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnătură: _________________________</p>
						</div>
						<div>
							<h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Prestator</h3>
							<p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnătură: _________________________</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrintableEntrySheet;


